import { observable, reaction, transaction } from 'mobx';
import {
    IMasterLayoutGuides,
    IAllMasterLayoutGuides,
} from '@src/slide/application/service/guides-manager/index.interface';
import {
    EPpGuideOrientation,
    IGuide,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import { Guide } from '@src/slide/domain/ui-model/element-modeles/guide-model';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import {
    DEFAULT_LAYOUT_GUIDE_COLOR,
    DEFAULT_MASTER_GUIDE_COLOR,
} from '@com/guides-line/guides-color-panel';
import { BaseGuidesService } from '@src/slide/application/service/guides-manager/base-guides-service';
import { IBaseMasterLayoutGuidesService } from '@src/slide/application/service/guides-manager/base-master-layout-guides-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IAddGuideProps } from '@src/slide/application/service/guides-manager/base-guides-service.interface';

interface IMasterLayoutGuidesServiceProps {
    selectionService: ISelectionService;
}

/**
 * 母版参考线
 */
export default abstract class BaeMasterLayoutGuidesService
    extends BaseGuidesService implements IBaseMasterLayoutGuidesService {
    /**
     * 所有的母版/板式参考线集合
     * 每个母版/板式都有自己的参考线
     */
    @observable public allGuides: Array<IAllMasterLayoutGuides> = [];

    public constructor(props: IMasterLayoutGuidesServiceProps) {
        super(props);
        reaction(() => this.selectionService.currentSlide, () => {
            const masterLayoutId = this.getId();
            transaction(() => {
                this.initVertAndHorz(masterLayoutId);
            });
        });
    }

    /**
     * 初始化母版参考线
     * @param guideList
     */
    public initGuides(guideList: Array<IMasterLayoutGuides>) {
        if (guideList?.length) {
            guideList.forEach((masterLayoutGuides) => {
                const { id, guides } = masterLayoutGuides;
                if (!id) return;
                const vertGuides: Array<Guide> = [];
                const horzGuides: Array<Guide> = [];

                guides.forEach((guide) => {
                    const { orient = EPpGuideOrientation.ppVerticalGuide, color } = guide;
                    const guideModel = new Guide();
                    guideModel.pos = Math.floor(guide.pos / 4);
                    guideModel.orient = orient;
                    guideModel.color = color;

                    // 过滤下重复数据
                    if (orient === EPpGuideOrientation.ppVerticalGuide) {
                        const index = vertGuides.findIndex(guide => guide.pos === guideModel.pos);
                        if (index === -1) vertGuides.push(guideModel);
                    } else {
                        const index = horzGuides.findIndex(guide => guide.pos === guideModel.pos);
                        if (index === -1) horzGuides.push(guideModel);
                    }
                });

                const cachedGuides = {
                    id,
                    vertGuides,
                    horzGuides,
                };
                const tempGuides = this.allGuides.find(tempGuides => tempGuides.id === id);
                if (tempGuides) {
                    tempGuides.vertGuides = cachedGuides.vertGuides || [];
                    tempGuides.horzGuides = cachedGuides.horzGuides || [];
                } else {
                    this.allGuides.push(cachedGuides);
                }

                if (id === this.getId()) {
                    transaction(() => {
                        this.vertGuides = vertGuides;
                        this.horzGuides = horzGuides;
                    });
                }
            });
        }
    }

    /**
     * 新增一个垂直参考线
     */
    public addVertGuides = (props: IAddGuideProps): void => {
        const { currentGuide } = props;
        const pos = this.computeAddVertPosition(this.vertGuides, props);
        if (pos > this.slideWidth || pos < 0) return;
        const guideModel = new Guide();

        guideModel.orient = EPpGuideOrientation.ppVerticalGuide;
        guideModel.color = currentGuide
            ? currentGuide.color
            : (this.isMaster()
                ? DEFAULT_MASTER_GUIDE_COLOR
                : DEFAULT_LAYOUT_GUIDE_COLOR);
        guideModel.pos = pos;
        this.vertGuides.push(guideModel);
        const id = this.getId();
        this.allGuides.forEach((masterLayoutGuides) => {
            if (id === masterLayoutGuides.id) {
                masterLayoutGuides.vertGuides = this.vertGuides;
            }
        });
        this.updateGuidesProps();
    };

    /**
     * 新增一个水平参考线
     */
    public addHorzGuides(props: IAddGuideProps) {
        const { currentGuide } = props;
        const pos = this.computeAddHorzPosition(this.horzGuides, props);
        if (pos > this.slideHeight || pos < 0) return;
        const guideModel = new Guide();

        guideModel.orient = EPpGuideOrientation.ppHorizontalGuide;
        guideModel.color = currentGuide
            ? currentGuide.color
            : (this.isMaster()
                ? DEFAULT_MASTER_GUIDE_COLOR
                : DEFAULT_LAYOUT_GUIDE_COLOR);
        guideModel.pos = pos;
        this.horzGuides.push(guideModel);
        const id = this.getId();
        this.allGuides.forEach((masterLayoutGuides) => {
            if (id === masterLayoutGuides.id) {
                masterLayoutGuides.horzGuides = this.horzGuides;
            }
        });
        this.updateGuidesProps();
    }

    /**
     * 删除参考线
     * @param guideModel
     */
    public deleteGuide(guideModel?: IGuide): void {
        if (!guideModel) return;
        const masterLayoutId = this.getId();
        const { orient } = guideModel;
        let guideList;
        if (orient === EPpGuideOrientation.ppVerticalGuide) {
            guideList = this.allGuides.find(masterLayoutGuides => masterLayoutGuides.id === masterLayoutId)?.vertGuides;
        } else {
            guideList = this.allGuides.find(masterLayoutGuides => masterLayoutGuides.id === masterLayoutId)?.horzGuides;
        }
        const index = guideList.findIndex(item => item === guideModel);
        if (index > -1) {
            guideList.splice(index, 1);
        }
        this.updateGuidesProps();
    }

    public updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio) {
        if (changeVertRadio || changeHorzRadio) {
            this.allGuides.forEach((masterLayoutGuides) => {
                const { vertGuides, horzGuides } = masterLayoutGuides;
                vertGuides.forEach((guide) => {
                    guide.pos = this.fixVertPos(guide.pos * changeVertRadio);
                });
                horzGuides.forEach((guide) => {
                    guide.pos = this.fixHorzPos(guide.pos * changeHorzRadio);
                });
            });
        }
    }

    public getUpdateProps() {
        return this.allGuides.map(guide => ({
            id: guide.id,
            guides: this.getGuides(guide.vertGuides, guide.horzGuides),
        })).filter(item => item.id !== undefined);
    }

    protected getGuides(vertGuides: Array<IGuide>, horzGuides: Array<IGuide>): Array<IGuide> {
        return [...vertGuides, ...horzGuides].map(guide => ({
            color: guide.color,
            pos: guide.pos * 4,
            orient: guide.orient,
            type: UIModelType.GUIDES,
        }));
    }

    /**
     * 解析垂直参考线/水平参考线
     * @param masterLayoutId
     * @private
     */
    private initVertAndHorz(masterLayoutId) {
        let masterLayoutGuides = this.allGuides.find(masterLayoutGuides => masterLayoutGuides.id === masterLayoutId);
        if (!masterLayoutGuides) {
            masterLayoutGuides = {
                id: masterLayoutId,
                vertGuides: [],
                horzGuides: [],
            };
            this.allGuides.push(masterLayoutGuides);
        }
        this.vertGuides = masterLayoutGuides?.vertGuides || [];
        this.horzGuides = masterLayoutGuides?.horzGuides || [];
    }

    protected abstract getId();
    protected abstract isMaster(): boolean;
}
